import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { articles, categories, site } from "@/mocks/portal";
import { usePageMeta, injectJsonLd, clearPageJsonLd } from "@/hooks/usePageMeta";

const catName = (slug: string) => categories.find((c) => c.slug === slug)?.name ?? "推荐";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((a) => a.id === id);
  const related = useMemo(() => {
    if (!article) return [];
    const same = articles.filter((a) => a.category === article.category && a.id !== article.id);
    const rest = articles.filter((a) => a.category !== article.category && a.id !== article.id);
    return [...same, ...rest].slice(0, 4);
  }, [article]);

  usePageMeta({
    title: article ? `${article.title} - 看料站` : "内容不存在 - 看料站",
    description: article ? article.summary : "没有找到这篇内容。",
    keywords: article ? `看料站,${article.title},${article.tags.join(",")}` : "看料站",
    path: `/article/${id}`,
  });

  useEffect(() => {
    if (!article) return;
    const cat = categories.find((c) => c.slug === article.category);
    const json = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://0ac9g.cn/" },
          { "@type": "ListItem", position: 2, name: cat?.name ?? "", item: `https://0ac9g.cn/column/${article.category}` },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
      {
        "@type": "Article",
        headline: article.title,
        description: article.summary,
        image: [article.image],
        datePublished: article.date,
        author: { "@type": "Person", name: article.author },
        articleSection: cat?.name,
        inLanguage: "zh-CN",
        publisher: { "@type": "Organization", name: site.name },
      },
    ];
    const el = injectJsonLd(json);
    return () => clearPageJsonLd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50 text-foreground-900">
        <SiteHeader />
        <main className="mx-auto flex min-h-[60vh] w-full max-w-[1180px] flex-col items-center justify-center px-4 text-center">
          <i className="ri-file-unknow-line text-5xl text-background-400" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇内容不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已被移除，或链接有误。</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            <i className="ri-arrow-left-line" /> 返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);

  return (
    <div className="flex min-h-screen flex-col bg-background-50 text-foreground-900">
      <SiteHeader />

      <main className="flex-1">
        <article className="mx-auto w-full max-w-[820px] px-4 py-10 md:px-6 md:py-14">
          <nav className="text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line mx-1" />
            <Link to={`/column/${article.category}`} className="hover:text-primary-700">
              {cat?.name}
            </Link>
            <i className="ri-arrow-right-s-line mx-1" />
            <span className="text-foreground-800">{article.title.slice(0, 10)}…</span>
          </nav>

          <header className="mt-7">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/column/${article.category}`} className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100">
                {cat?.name}
              </Link>
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-background-200/70 py-4 text-sm text-foreground-500">
              <span className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-user-line text-xs" />
                </span>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" /> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" /> {article.readTime}
              </span>
            </div>
          </header>

          <div className="mt-7 overflow-hidden rounded-lg">
            <img src={article.image} alt={article.title} className="aspect-[16/9] w-full object-cover object-top" />
          </div>

          <div className="mt-8 space-y-6">
            {article.body.map((b, i) =>
              b.type === "h2" ? (
                <h2 key={i} className="pt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {b.text}
                </h2>
              ) : (
                <p key={i} className="text-[15px] leading-8 text-foreground-800 md:text-base md:leading-9">
                  {b.text}
                </p>
              )
            )}
          </div>

          {/* 作者/分享 */}
          <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200/70 bg-background-100 p-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                {article.author[0]}
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">{article.author} · 责任编辑</p>
                <p className="text-xs text-foreground-500">看料站{cat?.name}栏目编辑</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-foreground-600">
              <span className="mr-1 text-xs text-foreground-500">分享给朋友</span>
              {["ri-wechat-line", "ri-weibo-line", "ri-qq-line", "ri-link-m"].map((ic) => (
                <button
                  key={ic}
                  type="button"
                  onClick={() => {
                    if (ic === "ri-link-m") navigator.clipboard?.writeText(window.location.href);
                  }}
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50"
                  aria-label="分享"
                >
                  <i className={`${ic} text-base`} />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-primary-50 px-5 py-4">
            <i className="ri-lightbulb-line text-xl text-primary-600" />
            <p className="flex-1 text-sm text-foreground-700">
              看料站坚持真实、审慎、正向的内容原则，不传播未经核实的针对个人的消息。若你发现内容有误，欢迎邮件反馈。
            </p>
          </div>
        </article>

        {/* 相关推荐 */}
        <section className="border-t border-background-200/60 bg-background-100/60" aria-label="相关推荐">
          <div className="mx-auto w-full max-w-[1180px] px-4 py-12 md:px-6">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest text-secondary-600">Keep Reading</span>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">继续阅读</h2>
              </div>
              <Link to={`/column/${article.category}`} className="hidden items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-600 sm:inline-flex">
                更多{cat?.name}内容 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </section>

        {/* 文末订阅 */}
        <section className="mx-auto w-full max-w-[1180px] px-4 py-12 md:px-6" id="article-sub">
          <NewsletterForm id="article-subscribe" title="喜欢这篇文章？" subtitle="订阅看料站精选，每周把值得读的内容送到你面前，随时可退订。" />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}